import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';

const API_URL = '/api';

function Register() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [credentials, setCredentials] = useState(null);
  const [copiedField, setCopiedField] = useState('');

  useEffect(() => {
    const hash = localStorage.getItem('avalw_account_hash');
    const type = localStorage.getItem('avalw_account_type');
    if (hash) {
      navigate(type === 'home' ? '/home-dashboard' : '/dashboard');
    }
  }, [navigate]);

  const handleRegister = async () => {
    setLoading(true);
    setError('');

    try {
      const res = await fetch(`${API_URL}/register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ account_type: 'home' })
      });
      const data = await res.json();

      if (data.success) {
        setCredentials({
          account_hash: data.account_hash,
          worker_token: data.worker_token
        });
      } else {
        setError(data.error || 'Registration failed');
      }
    } catch (err) {
      setError('Connection error. Please try again.');
    }

    setLoading(false);
  };

  const copyToClipboard = (text, field) => {
    navigator.clipboard.writeText(text);
    setCopiedField(field);
    setTimeout(() => setCopiedField(''), 2000);
  };

  const handleContinue = () => {
    localStorage.setItem('avalw_account_hash', credentials.account_hash);
    localStorage.setItem('avalw_worker_token', credentials.worker_token);
    localStorage.setItem('avalw_account_type', 'home');
    navigate('/home-dashboard');
  };

  const downloadCredentials = () => {
    const content = `AVALW Worker Credentials
========================
Generated: ${new Date().toISOString()}

Account Hash:
${credentials.account_hash}

Worker Token:
${credentials.worker_token}

IMPORTANT: Keep these credentials safe!
If you lose your Account Hash, you cannot recover your account.

Run Worker:
npx @avalw/search-worker --token ${credentials.worker_token}
`;
    const blob = new Blob([content], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'avalw-credentials.txt';
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="login-page">
      <div className="login-container">
        {/* Left Side - Branding */}
        <div className="login-branding register">
          <div className="login-brand-content">
            <div className="login-logo">
              <svg viewBox="0 0 40 40" fill="none">
                <circle cx="20" cy="20" r="18" stroke="currentColor" strokeWidth="2" fill="none"/>
                <circle cx="20" cy="20" r="8" fill="currentColor"/>
                <circle cx="20" cy="8" r="3" fill="currentColor"/>
                <circle cx="20" cy="32" r="3" fill="currentColor"/>
                <circle cx="8" cy="20" r="3" fill="currentColor"/>
                <circle cx="32" cy="20" r="3" fill="currentColor"/>
              </svg>
              <span>AVALW</span>
            </div>
            <h1>Join the Network</h1>
            <p>Create your account and start contributing to privacy-focused search.</p>

            <div className="login-steps">
              <div className="login-step">
                <div className="step-number">1</div>
                <div>
                  <strong>Create Account</strong>
                  <span>Get your unique credentials</span>
                </div>
              </div>
              <div className="login-step">
                <div className="step-number">2</div>
                <div>
                  <strong>Save Credentials</strong>
                  <span>Download and keep them safe</span>
                </div>
              </div>
              <div className="login-step">
                <div className="step-number">3</div>
                <div>
                  <strong>Run Worker</strong>
                  <span>Start earning rewards</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Right Side - Register Form */}
        <div className="login-form-side">
          <div className="login-card">
            {!credentials ? (
              <>
                <div className="login-header">
                  <h2>Create Account</h2>
                  <p>One click to join the AVALW network</p>
                </div>

                {error && (
                  <div className="login-error">
                    <svg viewBox="0 0 24 24" fill="currentColor">
                      <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z"/>
                    </svg>
                    {error}
                  </div>
                )}

                <div className="register-info">
                  <svg viewBox="0 0 24 24" fill="currentColor">
                    <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-6h2v6zm0-8h-2V7h2v2z"/>
                  </svg>
                  <p>No email or password required. Your account is identified by a unique hash that only you possess.</p>
                </div>

                <button onClick={handleRegister} className="login-btn primary large" disabled={loading}>
                  {loading ? (
                    <>
                      <span className="login-spinner"></span>
                      Creating Account...
                    </>
                  ) : (
                    <>
                      <svg viewBox="0 0 24 24" fill="currentColor">
                        <path d="M19 13h-6v6h-2v-6H5v-2h6V5h2v6h6v2z"/>
                      </svg>
                      Create My Account
                    </>
                  )}
                </button>

                <div className="login-divider">
                  <span>Already have an account?</span>
                </div>

                <Link to="/login" className="login-btn secondary">
                  Sign In
                </Link>
              </>
            ) : (
              <>
                <div className="login-header success">
                  <div className="success-icon">
                    <svg viewBox="0 0 24 24" fill="currentColor">
                      <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/>
                    </svg>
                  </div>
                  <h2>Account Created!</h2>
                  <p>Save your credentials below. You'll need them to log in.</p>
                </div>

                <div className="credentials-box">
                  <div className="credential-item">
                    <label>Account Hash</label>
                    <div className="credential-value">
                      <code>{credentials.account_hash}</code>
                      <button
                        onClick={() => copyToClipboard(credentials.account_hash, 'hash')}
                        className={copiedField === 'hash' ? 'copied' : ''}
                      >
                        {copiedField === 'hash' ? 'Copied!' : 'Copy'}
                      </button>
                    </div>
                  </div>

                  <div className="credential-item">
                    <label>Worker Token</label>
                    <div className="credential-value">
                      <code>{credentials.worker_token}</code>
                      <button
                        onClick={() => copyToClipboard(credentials.worker_token, 'token')}
                        className={copiedField === 'token' ? 'copied' : ''}
                      >
                        {copiedField === 'token' ? 'Copied!' : 'Copy'}
                      </button>
                    </div>
                  </div>

                  <div className="credential-warning">
                    <svg viewBox="0 0 24 24" fill="currentColor">
                      <path d="M1 21h22L12 2 1 21zm12-3h-2v-2h2v2zm0-4h-2v-4h2v4z"/>
                    </svg>
                    <span>Save these credentials! If you lose your Account Hash, you cannot recover your account.</span>
                  </div>
                </div>

                <button onClick={downloadCredentials} className="login-btn secondary">
                  <svg viewBox="0 0 24 24" fill="currentColor">
                    <path d="M19 9h-4V3H9v6H5l7 7 7-7zM5 18v2h14v-2H5z"/>
                  </svg>
                  Download Credentials
                </button>

                <button onClick={handleContinue} className="login-btn primary">
                  Continue to Dashboard
                  <svg viewBox="0 0 24 24" fill="currentColor">
                    <path d="M12 4l-1.41 1.41L16.17 11H4v2h12.17l-5.58 5.59L12 20l8-8z"/>
                  </svg>
                </button>
              </>
            )}

            <div className="login-footer">
              <Link to="/">Back to Home</Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Register;
